/* global globalThis */
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import OutlineButton from "../../../Components/Buttons/outline-button";
import PrimaryButton from "../../../Components/Buttons/primary-button";
import SelectField from "../../../Components/Form Fields/select-field";
import { Trans, useTranslation } from "react-i18next";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { LocalStorage, transformUSNumberToCountryCodeFormat, removeControlCharacters, getUserId } from "../../../Utils/helpers";
import { maskEmail, maskPhone, maskSSN } from "../../../Utils/pii";
import { validation } from "../../../Utils/validations";
import { validateFlowSchema } from "./validateSchema";
import { STEP_IDS } from "./schema";
import { useDirectSignupCustomer, useRequestLogin2FACode, useLoginWith2FACode } from "../../../CustomHooks/Mutations";
import { useAppUtilityContext } from "../../../Contexts/AppUtilityContext";
import { useDeals } from "../../../CustomHooks/Queries";
import AddressAutoCompleteField from "../../../Components/Form Fields/address-autocomplete-field";
import { appUtilityKeys, debugProps, phoneNumberMask, UnitOccupationList } from "../../../Utils/constants";
import { IMaskInput } from "react-imask";
import { APIhandler } from "../../../Utils/api";
import { sendSoftSignupUpdate } from "./utils/softSignupTracking";
import { trackLeadEvent, getLeadEventName } from "../../../Utils/meta-pixel";

function useStableMessages(initialMessages = []) {
  const [messages, setMessages] = useState(() => initialMessages.map((msg) => ({ ...msg, hasAnimated: !!msg.hasAnimated })));
  const push = useCallback((message) => {
    setMessages((arr) => [...arr, { ...message, hasAnimated: !!message.hasAnimated }]);
  }, []);
  const markAnimated = useCallback((id) => {
    setMessages((arr) =>
      arr.map((msg) => (msg.id === id && !msg.hasAnimated ? { ...msg, hasAnimated: true } : msg))
    );
  }, []);
  return { messages, push, setMessages, markAnimated };
}

const hasStructuredClone = typeof globalThis !== "undefined" && typeof globalThis.structuredClone === "function";

const cloneAnswers = (value) => {
  const source = value || {};
  if (hasStructuredClone) {
    try {
      return globalThis.structuredClone(source);
    } catch {
      // fall through to JSON clone
    }
  }
  try {
    return JSON.parse(JSON.stringify(source));
  } catch {
    return { ...source };
  }
};

const deepFreeze = (obj) => {
  if (!obj || typeof obj !== "object" || Object.isFrozen(obj)) return obj;
  Object.getOwnPropertyNames(obj).forEach((prop) => {
    deepFreeze(obj[prop]);
  });
  return Object.freeze(obj);
};

const snapshotAnswers = (value) => deepFreeze(cloneAnswers(value));

const getNextId = (step, answersForNext) => {
  if (!step || !step.next) return step?.next;
  if (typeof step.next === "function") {
    return step.next(snapshotAnswers(answersForNext || {}));
  }
  return step.next;
};

const mergeAnswerObjects = (target, source) => {
  const next = { ...(target || {}) };
  Object.entries(source || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const existing = Array.isArray(next[key]) ? next[key] : [];
      next[key] = Array.from(new Set([...existing, ...value]));
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      next[key] = mergeAnswerObjects(
        typeof next[key] === "object" && next[key] !== null && !Array.isArray(next[key]) ? next[key] : {},
        value
      );
    } else {
      next[key] = value;
    }
  });
  return next;
};

const PRIVACY_URL = "https://getyourstake.com/legal/privacy-policy";
const TERMS_URL = "https://getyourstake.com/legal/end-user-terms";

const PHONE_ECHO_STEPS = new Set([
  STEP_IDS.RENTER_PHONE_CALL,
  STEP_IDS.RENTER_PHONE_TEXT,
  STEP_IDS.CASHBACK_PHONE_INPUT
]);

const formatEchoPayload = (stepId, payload) => {
  if (!payload) return "";
  if (stepId === STEP_IDS.RENTER_MOVE_IN) return payload?.moveInDate ?? "";
  if (stepId === STEP_IDS.CASHBACK_BANK_SSN) return maskSSN(payload?.ssn);
  if (PHONE_ECHO_STEPS.has(stepId)) return maskPhone(payload?.phone);
  if (stepId === STEP_IDS.RENTER_EMAIL) return maskEmail(payload?.email);
  if (stepId === STEP_IDS.GLOBAL_MANUAL_CONTACT) {
    const { name, email, phone } = payload;
    return [name, maskEmail(email), maskPhone(phone)].filter(Boolean).join(", ");
  }
  if (stepId === STEP_IDS.CASHBACK_BANK_ADDRESS) {
    // Format address without the addressSelectedFromAutocomplete boolean flag
    const { address, city, state, zipCode } = payload;
    return [address, city, state, zipCode].filter(Boolean).join(", ");
  }
  return Object.values(payload || {}).join(", ");
};

// Helper function to send contact info to API with user's path
const sendContactInfoToAPI = (contactInfo, answers, currentStepId) => {
  const { name, email, phone } = contactInfo;
  if (!name && !email && !phone) return; // Skip if no contact info
  
  // Get mobile user id from localStorage (available after 2FA login)
  const userId = getUserId();
  
  // Build path information
  const path = {
    persona: answers?.persona || null,
    renterReasons: answers?.renterReasons || [],
    steps: Object.keys(answers || {}).filter(key => 
      key !== 'persona' && key !== 'renterReasons' && 
      !key.includes('Details') && !key.includes('Contact')
    ),
    currentStep: currentStepId,
  };
  
  // Prepare payload
  const payload = {
    name: name || null,
    email: email || null,
    phone: phone || null,
    userId: userId || null, // Include mobile user id if available
    path: path,
  };
  
  // Send to API (fire and forget - don't block UI)
  APIhandler.onboarding.contactInfoFromChatOnboarding(payload).catch((error) => {
    console.error("Failed to send contact info to API", error);
  });
};

export default function ChatEngine({ schema, onIdentityGoogle, onComplete }) {
  // Validate once on mount; throw to fail build if schema invalid
  validateFlowSchema(schema);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation("welcome");
  const { onUserCredentialsUpdate, appUtilityProps } = useAppUtilityContext();
  // Only fetch deals data after user is authenticated (has authKey)
  const authKey = appUtilityProps?.authKey || LocalStorage.read(appUtilityKeys.authKey);
  const { data: dealsData } = useDeals({ enabled: !!authKey });
  const schemaVersion = schema?.schemaVersion || "0.0.0";
  const sessionStartRef = useRef(Date.now());
  const hasCompletedRef = useRef(false);
  const last2FAErrorIdRef = useRef(null); // Track last 2FA error message ID to prevent spamming
  
  // API mutations for cashback flow
  const { mutate: directSignupCustomer, isLoading: isSigningUp } = useDirectSignupCustomer();
  const { mutate: requestLogin2FACode, isLoading: isRequesting2FA } = useRequestLogin2FACode();
  const { mutate: loginWith2FACode, isLoading: isVerifying2FA } = useLoginWith2FACode();
  const track = (eventName, props = {}) => {
    try {
      if (typeof window !== "undefined" && window.heap && typeof window.heap.track === "function") {
        window.heap.track(eventName, { schemaVersion, lang: i18n?.language || "en", ...props });
      }
    } catch {
      // no-op
    }
  };
  useEffect(() => {
    track("welcome_viewed", { knownUser: !!LocalStorage.read("user") });
    // Send soft_signup update immediately on page load (empty conversionState for initial tracking)
    sendSoftSignupUpdate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [answers, setAnswers] = useState(() => LocalStorage.read("welcome_flow_answers") || {});
  const getStepById = (id) => schema.steps.find((s) => s.id === id);
  const firstStep = schema.steps[0];
  const initialStepId = (() => {
    // Check pathname first for deep links (most reliable)
    if (location.pathname === "/welcome/find-a-home") {
      // Ensure answers are set for deep link
      const existingAnswers = LocalStorage.read("welcome_flow_answers") || {};
      if (!existingAnswers.persona || !existingAnswers.renterReasons?.includes("find-home")) {
        const deepLinkAnswers = {
          ...existingAnswers,
          persona: "renter",
          renterReasons: ["find-home"],
          useCase: "renter_find_home",
        };
        LocalStorage.write("welcome_flow_answers", deepLinkAnswers);
      }
      LocalStorage.write("welcome_flow_greet_done", true);
      return STEP_IDS.RENTER_FIND_HOME_CONSENT;
    }
    // Check for custom initial step (used for deep links) - read fresh from localStorage
    const customInitialStep = LocalStorage.read("welcome_flow_custom_initial_step");
    if (customInitialStep) {
      return customInitialStep;
    }
    const greetDone = LocalStorage.read("welcome_flow_greet_done");
    if (firstStep?.id === STEP_IDS.GLOBAL_GREET && greetDone) {
      const nextId = getNextId(firstStep, answers);
      return nextId || firstStep.id;
    }
    return firstStep?.id;
  })();
  
  // Update answers state if deep link values were set
  useEffect(() => {
    if (location.pathname === "/welcome/find-a-home") {
      const storedAnswers = LocalStorage.read("welcome_flow_answers") || {};
      if (storedAnswers.persona === "renter" && storedAnswers.renterReasons?.includes("find-home")) {
        setAnswers(storedAnswers);
      }
    }
  }, [location.pathname]);
  
  // Remove custom initial step after we've used it (use effect to ensure it happens after render)
  useEffect(() => {
    if (initialStepId && initialStepId !== firstStep?.id) {
      const customInitialStep = LocalStorage.read("welcome_flow_custom_initial_step");
      if (customInitialStep === initialStepId) {
        LocalStorage.remove("welcome_flow_custom_initial_step");
      }
    }
  }, []); // Run once on mount
  const [currentStepId, setCurrentStepId] = useState(initialStepId);
  const [isSubmittingDOB, setIsSubmittingDOB] = useState(false);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [isProcessingApplication, setIsProcessingApplication] = useState(false);
  const applicationStartTimeRef = useRef(null);
  const shownSystemIdsRef = useRef(new Set());
  const animatedIdsRef = useRef(new Set());
  const typedDoneRef = useRef(new Set());
  const [, forceRerender] = useState(0);
  const bottomRef = useRef(null);
  const controlsRef = useRef(null);
  const leadEventFiredRef = useRef(false); // Track if lead event has been fired to prevent duplicates

  const currentStep = useMemo(() => {
    const step = schema.steps.find((s) => s.id === currentStepId);
    if (!step) return null;
    // Resolve function-based options if needed
    if (step.options && typeof step.options === "function") {
      return { ...step, options: step.options(answers) };
    }
    return step;
  }, [schema, currentStepId, answers]);

  // Initialize messages with the first step immediately to avoid delay
  const initialMessage = (() => {
    if (!firstStep || firstStep.id !== initialStepId) return null;
    
    // Check localStorage FIRST to prevent duplicate greeting on re-renders (e.g., React StrictMode)
    const greetDone = LocalStorage.read("welcome_flow_greet_done");
    if (firstStep.id === STEP_IDS.GLOBAL_GREET && greetDone) {
      // If greet is done, mark it in ref too to prevent any race conditions
      shownSystemIdsRef.current.add(firstStep.id);
      return null;
    }
    
    // Double-check ref to prevent race conditions during StrictMode remounts
    if (shownSystemIdsRef.current.has(firstStep.id)) return null;
    
    if (firstStep.promptKey) {
      shownSystemIdsRef.current.add(firstStep.id);
      const promptParams = typeof firstStep.promptKey === "function" ? null : (firstStep.promptParams ? (typeof firstStep.promptParams === "function" ? firstStep.promptParams(answers) : firstStep.promptParams) : null);
      if (firstStep.id === STEP_IDS.GLOBAL_GREET) LocalStorage.write("welcome_flow_greet_done", true);
      return { role: "system", promptKey: firstStep.promptKey, promptParams, id: firstStep.id };
    }
    return null;
  })();

  // Initialize messages state with initial message so it's available on first render
  const { messages, push, setMessages, markAnimated } = useStableMessages(initialMessage ? [initialMessage] : []);

  useEffect(() => {
    if (!currentStep) return;
    // Skip if this is the initial step (already handled above in state initialization)
    if (currentStep.id === initialStepId && initialMessage) return;
    
    // Skip GLOBAL_END message for owner/operator personas (they already got contactComplete message)
    if (currentStep.id === STEP_IDS.GLOBAL_END && (answers?.persona === "owner" || answers?.persona === "mom_and_pop" || answers?.persona === "large_owner")) {
      shownSystemIdsRef.current.add(currentStep.id);
      return;
    }
    
    if (currentStep.promptKey && !shownSystemIdsRef.current.has(currentStep.id)) {
      // Check if the prompt is empty/silent - if so, skip rendering and auto-advance
      const promptText = typeof currentStep.promptKey === "function" 
        ? currentStep.promptKey(answers) 
        : (currentStep.promptParams 
          ? (typeof currentStep.promptParams === "function" ? t(currentStep.promptKey, currentStep.promptParams(answers)) : t(currentStep.promptKey, currentStep.promptParams))
          : t(currentStep.promptKey));
      
      if (!promptText || promptText.trim() === "") {
        // Silent router step - mark as shown and auto-advance
        shownSystemIdsRef.current.add(currentStep.id);
        if (currentStep.type === "system") {
          resolveNext();
        }
        return;
      }
      
      shownSystemIdsRef.current.add(currentStep.id);
      const promptParams = typeof currentStep.promptKey === "function" ? null : (currentStep.promptParams ? (typeof currentStep.promptParams === "function" ? currentStep.promptParams(answers) : currentStep.promptParams) : null);
      push({ role: "system", promptKey: currentStep.promptKey, promptParams, id: currentStep.id });
      if (currentStep.id === STEP_IDS.GLOBAL_GREET) LocalStorage.write("welcome_flow_greet_done", true);
    }
    // Do not auto-advance here; we advance when typewriter finishes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId]);
  // Track step viewed and completion
  useEffect(() => {
    if (!currentStep) return;
    const hasAnswer = currentStep.answerKey ? !!(answers && answers[currentStep.answerKey]) : false;
    track("welcome_step_viewed", { stepId: currentStep.id, type: currentStep.type, hasAnswer });
    if (currentStep.id === STEP_IDS.GLOBAL_END && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      const durationMs = Date.now() - (sessionStartRef.current || Date.now());
      const persona = answers?.persona || "unknown";
      const reasons = answers?.renterReasons || [];
      let path = persona;
      if (persona === "renter") {
        const tags = [];
        if (reasons.includes("cashback")) tags.push("cashback");
        if (reasons.includes("find-home")) tags.push("find-home");
        if (reasons.includes("credit")) tags.push("credit");
        path = `renter:${tags.join("+") || "other"}`;
      } else if (persona === "owner" || persona === "mom_and_pop" || persona === "large_owner") {
        path = "owner";
      }
      track("welcome_completed", {
        path,
        durationMs,
        collectedKeys: Object.keys(answers || {}).length
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId]);

  const resolveNext = (extraPatch = {}) => {
    const merged = mergeAnswerObjects(answers, extraPatch);
    setAnswers(merged);
    LocalStorage.write("welcome_flow_answers", merged);

    let nextId = getNextId(currentStep, merged);
    if (!nextId) return; // end step might not have next
    setCurrentStepId(nextId);
  };

  const [awaitingOther, setAwaitingOther] = useState(false);
  const [singleOtherValue, setSingleOtherValue] = useState("");
  useEffect(() => {
    setAwaitingOther(false);
    setSingleOtherValue("");
  }, [currentStepId]);

  const markPiiConsentAcknowledged = useCallback(() => {
    if (answers?.piiConsentAcknowledged) return;
    const updated = mergeAnswerObjects(answers, { piiConsentAcknowledged: true });
    setAnswers(updated);
    LocalStorage.write("welcome_flow_answers", updated);
  }, [answers]);

  const onChooseSingle = (opt) => {
    if (currentStep.answerKey) {
      const basePatch = { [currentStep.answerKey]: opt.id };
      const customPatch = opt.answerPatch ? mergeAnswerObjects({}, opt.answerPatch) : {};
      const patch = mergeAnswerObjects(basePatch, customPatch);
      const merged = mergeAnswerObjects(answers, patch);
      LocalStorage.write("welcome_flow_answers", merged);
      setAnswers(merged);
      // Send soft_signup update with current conversion state
      sendSoftSignupUpdate(merged);
      const stepNext = getNextId(currentStep, merged);
      const nextId = opt.next || stepNext;
      // Analytics for choice selection and identity initiation
      if (currentStep.id === STEP_IDS.GLOBAL_IDENTITY) {
        track("welcome_identity_started", { method: opt.id });
      }
      track("welcome_option_selected", { stepId: currentStep.id, optionId: opt.id, nextStepId: nextId });
      const advance = () => setCurrentStepId(nextId);
      if (opt.isOther) {
        // Stay on this step and show text input for "Other"
        setAwaitingOther(true);
        setSingleOtherValue("");
        return;
      }
      // For Google identity, do not echo or advance until OAuth finishes
      if (opt.id === "google" && onIdentityGoogle) {
        onIdentityGoogle().finally(() => {
          advance();
        });
      } else if (currentStep.id === STEP_IDS.CASHBACK_BANK_DISCLOSURES && opt.id === "agree") {
        // When user agrees to disclosures, navigate to checkout page
        // Store the answer
        const basePatch = { [currentStep.answerKey]: opt.id };
        const merged = mergeAnswerObjects(answers, basePatch);
        LocalStorage.write("welcome_flow_answers", merged);
        setAnswers(merged);
        
        // Echo user choice
        if (opt.labelKey) {
          push({ 
            role: "user", 
            translationKey: opt.labelKey,
            id: `${currentStep.id}-echo` 
          });
        }
        
        // Map occupation ID back to original value (backend expects the value, not the lowercase ID)
        const storedOccupationId = merged?.bankOccupation;
        let occupationValue = storedOccupationId;
        if (storedOccupationId) {
          const occupationOption = UnitOccupationList.find((o) => {
            const optionId = String(o.value ?? o.id ?? o.label).toLowerCase().replace(/[^a-z0-9]+/gi, "").trim();
            return optionId === storedOccupationId;
          });
          if (occupationOption) {
            occupationValue = occupationOption.value ?? occupationOption.id ?? occupationOption.label;
          }
        }
        
        // Navigate to checkout page with welcome flow data
        // The API call will happen when user completes checkout
        const welcomeFlowData = {
          fromWelcome: true,
          occupation: occupationValue,
          address: merged?.bankAddress?.address,
          city: merged?.bankAddress?.city,
          state: merged?.bankAddress?.state,
          zipCode: merged?.bankAddress?.zipCode,
          ssn: merged?.bankSSN?.ssn,
          dob: merged?.bankDOB?.dob,
        };
        
        navigate("/deals/stake-saver", { state: welcomeFlowData });
        return; // Don't advance normally, we're navigating away
      } else {
        // Echo only for non-Google choices - store translation key for re-translation on language change
        if (opt.labelKey) {
          push({ 
            role: "user", 
            translationKey: opt.labelKey,
            id: `${currentStep.id}-echo` 
          });
        } else if (opt.label) {
          push({ role: "user", text: opt.label, id: `${currentStep.id}-echo` });
        }
        advance();
      }
    } else {
      resolveNext();
    }
  };

  const handleSingleOtherSubmit = () => {
    const trimmed = singleOtherValue.trim();
    if (!trimmed) return;
    const val = `other:${trimmed}`;
    const patch = { [currentStep.answerKey]: val };
    const merged = mergeAnswerObjects(answers, patch);
    setAnswers(merged);
    LocalStorage.write("welcome_flow_answers", merged);
    // Ensure options is an array before using find
    const options = Array.isArray(currentStep.options) ? currentStep.options : [];
    const otherOption = options.find((o) => o.id === "other");
    // Store translation key and other value separately for re-translation on language change
    push({
      role: "user",
      translationKey: otherOption?.labelKey,
      otherValue: trimmed,
      id: `${currentStep.id}-echo`
    });
    const nextId = getNextId(currentStep, merged);
    setAwaitingOther(false);
    // For why steps, route to GLOBAL_END after "other" submission
    if (currentStep.id === STEP_IDS.CASHBACK_ACCOUNT_WHY || currentStep.id === STEP_IDS.CASHBACK_READY_WHY) {
      setCurrentStepId(STEP_IDS.GLOBAL_END);
    } else {
      setCurrentStepId(nextId);
    }
  };

  const [multi, setMulti] = useState([]);
  const [otherValue, setOtherValue] = useState("");
  const [singleSelectValue, setSingleSelectValue] = useState("");
  useEffect(() => {
    setMulti([]);
    setOtherValue("");
    setSingleSelectValue("");
  }, [currentStepId]);

  const toggleMulti = (id) => {
    setMulti((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const onSubmitMulti = () => {
    let val = [...multi];
    if (multi.includes("other") && otherValue.trim()) {
      val = val.filter((v) => v !== "other");
      val.push(`other:${otherValue.trim()}`);
    }
    
    // Validate neighborhoods step - require at least 1 selection
    if (currentStep.id === STEP_IDS.RENTER_NEIGHBORHOODS && val.length === 0) {
      push({ role: "system", text: t("findHome.neighborhoodsRequired"), id: `neighborhoods-required-${Date.now()}` });
      return;
    }
    
    const patch = { [currentStep.answerKey]: val };
    // echo user selection - store option IDs for re-translation on language change
    const optionIds = val.map((v) => {
      if (v.startsWith("other:")) {
        return { type: "other", otherValue: v.split(":").slice(1).join(":") };
      }
      return { type: "option", optionId: v };
    });
    push({
      role: "user",
      stepId: currentStep.id,
      optionIds: optionIds,
      id: `${currentStep.id}-echo`
    });
    track("welcome_input_submitted", { stepId: currentStep.id, answerKey: currentStep.answerKey, selectionCount: val.length });
    const merged = mergeAnswerObjects(answers, patch);
    // Send soft_signup update with current conversion state
    sendSoftSignupUpdate(merged);
    resolveNext(patch);
  };

  // Initialize defaults for special fields on step entry
  useEffect(() => {
    if (!currentStep) return;
    // default move-in date
    if (currentStep.id === STEP_IDS.RENTER_MOVE_IN && currentStep.type === "text") {
      const cur = LocalStorage.read("welcome_flow_answers") || {};
      const existing = cur?.moveIn?.moveInDate;
      if (!existing) {
        const now = new Date();
        const d = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const iso = `${yyyy}-${mm}-${dd}`;
        const next = { ...answers, moveIn: { ...(answers.moveIn || {}), moveInDate: iso } };
        setAnswers(next);
        LocalStorage.write("welcome_flow_answers", next);
      }
    }
    // default price for slider: set immediately so value shows without interaction
    if (currentStep.type === "slider" && currentStep.answerKey) {
      const cur = LocalStorage.read("welcome_flow_answers") || {};
      const existing = cur?.[currentStep.answerKey];
      const def = currentStep.slider?.default ?? 1500;
      if (existing == null) {
        const next = { ...answers, [currentStep.answerKey]: def };
        setAnswers(next);
        LocalStorage.write("welcome_flow_answers", next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId]);

  const onSliderChange = (val) => {
    const value = Number(val);
    setAnswers((a) => {
      const next = mergeAnswerObjects(a, { [currentStep.answerKey]: value });
      LocalStorage.write("welcome_flow_answers", next);
      return next;
    });
  };

  // Auto-scroll to keep latest question/choices in view
  useEffect(() => {
    const timer = setTimeout(() => {
      const el = bottomRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const overflow = rect.top + 24 - window.innerHeight;
      if (overflow > 0) {
        window.scrollBy({ top: overflow, behavior: "smooth" });
      }
    }, 60);
    return () => clearTimeout(timer);
  }, [messages.length, currentStepId, awaitingOther, multi.length, singleOtherValue]);

  // Helper function to translate user response messages based on stored translation keys/option IDs
  const translateUserMessage = useCallback((m) => {
    // If message has translationKey, translate it
    if (m.translationKey) {
      const translated = t(m.translationKey);
      if (m.otherValue) {
        return `${translated}: ${m.otherValue}`;
      }
      return translated;
    }
    
    // If message has optionIds (for multi-choice), translate each option
    if (m.optionIds && Array.isArray(m.optionIds)) {
      const step = schema.steps.find((s) => s.id === m.stepId);
      if (step && step.options) {
        // Resolve function-based options if needed
        const resolvedOptions = typeof step.options === "function" 
          ? step.options(answers) 
          : step.options;
        
        // Ensure options is an array before using find
        if (!Array.isArray(resolvedOptions)) {
          return m.text || "";
        }
        
        return m.optionIds
          .map((item) => {
            if (item.type === "other") {
              return `${t("reasons.other")}: ${item.otherValue}`;
            } else if (item.type === "option") {
              const opt = resolvedOptions.find((o) => o.id === item.optionId);
              return opt?.labelKey ? t(opt.labelKey) : (opt?.label || "");
            }
            return "";
          })
          .filter(Boolean)
          .join(", ");
      }
    }
    
    // Fallback to stored text if no translation metadata
    return m.text || "";
  }, [t, schema, answers]);

  // Force re-render when language changes to update user response translations
  useEffect(() => {
    forceRerender((x) => x + 1);
  }, [i18n.language, forceRerender]);

  if (!currentStep) return null;

  function Typewriter({
    id,
    promptKey,
    promptParams,
    hasAnimated,
    onAnimated,
    answers: answersSnapshot,
    animatedIdsRef: animatedRef,
    typedDoneRef: typedRef,
    currentStep: currentStepSnapshot,
    resolveNext: resolveNextFn,
    forceRerender: forceRerenderFn,
    t: translate,
    bottomRef: scrollRef
  }) {
    const fullText =
      typeof promptKey === "function" ? promptKey(answersSnapshot) : promptParams ? translate(promptKey, promptParams) : translate(promptKey);
    const alreadyAnimated = hasAnimated || animatedRef.current.has(id);
    const [out, setOut] = useState(alreadyAnimated ? fullText : "");
    const [showLoadingDots, setShowLoadingDots] = useState(!alreadyAnimated && fullText.length > 0);
    useEffect(() => {
      if (alreadyAnimated) {
        setOut(fullText);
        setShowLoadingDots(false);
        return;
      }
      let i = 0;
      const normalSpeed = 5;
      const initialDelay = 750; // 750ms delay before starting typewriter animation
      // For the first greeting message, start with 0ms delay, then switch to normal speed after first character
      const isFirstGreeting = currentStepSnapshot?.id === STEP_IDS.GLOBAL_GREET;
      let speed = isFirstGreeting ? 0 : normalSpeed;
      let timeoutId = null;
      
      const tick = () => {
        // Hide loading dots when we start typing
        setShowLoadingDots(false);
        i += 1;
        setOut(fullText.slice(0, i));
        
        // After first character (or specifically after "i" in "Hi"), switch to normal speed
        if (isFirstGreeting && i >= 2 && speed === 0) {
          speed = normalSpeed;
        }
        
        if (i >= fullText.length) {
          animatedRef.current.add(id);
          typedRef.current.add(id);
          onAnimated?.(id);
          if (currentStepSnapshot?.id === id && currentStepSnapshot?.type === "system") {
            resolveNextFn();
          } else {
            forceRerenderFn((x) => x + 1);
          }
          setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 0);
        } else {
          timeoutId = setTimeout(tick, speed);
        }
      };
      
      // Start the first tick after initialDelay (500ms), then continue with normal speed
      timeoutId = setTimeout(tick, initialDelay + (isFirstGreeting ? 0 : normalSpeed));
      
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alreadyAnimated, fullText, id]);
    useEffect(() => {
      if (alreadyAnimated) {
        setOut(fullText);
        setShowLoadingDots(false);
      }
    }, [alreadyAnimated, fullText]);
    
    // Show loading dots during initial delay, otherwise show typewriter text
    if (showLoadingDots) {
      return (
        <p className="typewriter">
          <span className="loading-dots">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </p>
      );
    }
    
    return <p className="typewriter">{out}</p>;
  }

  return (
    <>
      {messages.map((m) => {
        // Skip rendering empty/silent messages
        if (m.promptKey) {
          const promptText = typeof m.promptKey === "function" 
            ? m.promptKey(answers) 
            : (m.promptParams 
              ? (typeof m.promptParams === "function" ? t(m.promptKey, m.promptParams) : t(m.promptKey, m.promptParams))
              : t(m.promptKey));
          if (!promptText || (typeof promptText === "string" && promptText.trim() === "")) {
            return null;
          }
        } else if (m.role === "user") {
          // For user messages, check if they have translation metadata or text
          const userText = translateUserMessage(m);
          if (!userText || (typeof userText === "string" && !userText.trim())) {
            return null;
          }
        } else if (m.text && (typeof m.text !== "string" || !m.text.trim() || m.text.trim() === "")) {
          return null;
        }
        
        // Find the step that corresponds to this message to check for agreements
        const messageStep = schema.steps.find((s) => s.id === m.id);
        const hasAgreements = messageStep?.agreements && messageStep.agreements.length > 0;
        const isTypewriterDone = typedDoneRef.current.has(m.id) || m.hasAnimated;
        
        return (
          <div key={m.id} className={`stake-ai-bubble ${m.role} visible`}>
            {m.role === "system" && (
              <div className="avatar">
                <img src="/favicon-32x32.png" alt="Stake" />
              </div>
            )}
            <div className="bubble-inner">
              {m.promptKey ? (
                <>
                  <Typewriter
                    id={m.id}
                    promptKey={m.promptKey}
                    promptParams={m.promptParams}
                    hasAnimated={m.hasAnimated}
                    onAnimated={markAnimated}
                    answers={answers}
                    animatedIdsRef={animatedIdsRef}
                    typedDoneRef={typedDoneRef}
                    currentStep={currentStep}
                    resolveNext={resolveNext}
                    forceRerender={forceRerender}
                    t={t}
                    bottomRef={bottomRef}
                  />
                  {hasAgreements && isTypewriterDone && (
                    <div className="stake-ai-agreements" style={{ marginTop: "16px" }}>
                      <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {messageStep.agreements.map((agreement) => (
                          <li key={agreement.id} style={{ marginBottom: "4px" }}>
                            <a href={agreement.url} target="_blank" rel="noreferrer" style={{ color: "#62f3a7" }}>
                              {agreement.labelKey ? t(agreement.labelKey) : agreement.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <p>{m.role === "user" ? translateUserMessage(m) : (m.text || "")}</p>
              )}
            </div>
          </div>
        );
      })}

      {(() => {
        if (currentStep.type === "singleChoice" && typedDoneRef.current.has(currentStep.id)) {
          return (
            <SingleChoiceStep
              ref={controlsRef}
              step={currentStep}
              t={t}
              onChooseSingle={onChooseSingle}
              awaitingOther={awaitingOther}
              singleOtherValue={singleOtherValue}
              setSingleOtherValue={setSingleOtherValue}
              onSubmitOther={handleSingleOtherSubmit}
              singleSelectValue={singleSelectValue}
              setSingleSelectValue={setSingleSelectValue}
              showPiiNotice={currentStep.showPiiNotice}
              piiConsentAcknowledged={!!answers?.piiConsentAcknowledged}
              onShowPiiNotice={markPiiConsentAcknowledged}
            />
          );
        }

        if (currentStep.type === "slider" && typedDoneRef.current.has(currentStep.id)) {
          const val = answers?.[currentStep.answerKey] ?? currentStep.slider?.default ?? 1500;
          const unit = currentStep.slider?.unit;
          const formatted = unit === "USD"
            ? (() => {
                try {
                  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(val);
                } catch {
                  return `$${val}`;
                }
              })()
            : String(val);

          const handleSliderSubmit = () => {
            // Slider values are numbers/formatted currency, no translation needed
            push({ role: "user", text: formatted, id: `${currentStep.id}-echo` });
            const merged = mergeAnswerObjects(answers, { [currentStep.answerKey]: val });
            // Send soft_signup update with current conversion state
            sendSoftSignupUpdate(merged);
            resolveNext({ [currentStep.answerKey]: val });
          };
          return (
            <SliderStep
              ref={controlsRef}
              step={currentStep}
              t={t}
              value={val}
              formatted={formatted}
              onSliderChange={onSliderChange}
              onSubmit={handleSliderSubmit}
            />
          );
        }

        if (currentStep.type === "multiChoice" && typedDoneRef.current.has(currentStep.id)) {
          // Ensure options is resolved (handle function-based options)
          const resolvedStep = currentStep.options && typeof currentStep.options === "function"
            ? { ...currentStep, options: currentStep.options(answers) }
            : currentStep;
          return (
            <MultiChoiceStep
              ref={controlsRef}
              step={resolvedStep}
              t={t}
              selections={multi}
              toggleSelection={toggleMulti}
              otherValue={otherValue}
              setOtherValue={setOtherValue}
              onSubmit={onSubmitMulti}
            />
          );
        }

        if (currentStep.type === "text" && typedDoneRef.current.has(currentStep.id)) {
          const handleAdvanceTextStep = () => {
            const payload = answers[currentStep.answerKey] || {};
            const emailFields = currentStep.fields?.filter((f) => f.validateEmail) || [];
            if (emailFields.length) {
              const invalidField = emailFields.find((f) => {
                const val = (payload?.[f.id] || "").trim();
                return !val || !validation.email.isAValidEmail(val);
              });
              if (invalidField) {
                push({ role: "system", text: t("validation.emailInvalid"), id: `email-invalid-${Date.now()}` });
                return;
              }
            }
            
            // Don't add echo for 2FA step - will be added on success only
            if (currentStep.id !== STEP_IDS.CASHBACK_2FA_VERIFY) {
              const echo = formatEchoPayload(currentStep.id, payload);
              if (echo) push({ role: "user", text: echo, id: `${currentStep.id}-echo` });
            }

            // Validate name and email step
            if (currentStep.id === STEP_IDS.RENTER_NAME_EMAIL) {
              const name = payload?.name || "";
              const email = payload?.email || "";
              
              // Validate full name - must have at least first name and last name
              if (!name || name.trim().length === 0) {
                push({ role: "system", text: t("manual.fullNameRequired"), id: `full-name-required-${Date.now()}` });
                return;
              }
              const nameParts = name.trim().split(/\s+/).filter(part => part.length > 0);
              if (nameParts.length < 2) {
                push({ role: "system", text: t("manual.fullNameRequired"), id: `full-name-required-${Date.now()}` });
                return;
              }
              
              // Validate email
              if (!email || !validation.email.isAValidEmail(email.trim())) {
                push({ role: "system", text: t("validation.emailInvalid"), id: `email-invalid-${Date.now()}` });
                return;
              }
            }
            
            // Simple validation: SSN must be 9 digits
            let valid = true;
            if (currentStep.id === STEP_IDS.CASHBACK_BANK_SSN) {
              const ssnDigits = String(payload?.ssn || "").replace(/\D/g, "");
              if (ssnDigits.length !== 9) {
                push({ role: "system", text: t("bank.ssnInvalid"), id: `ssn-invalid-${Date.now()}` });
                valid = false;
                return;
              }
            }
            track("welcome_input_submitted", { stepId: currentStep.id, answerKey: currentStep.answerKey, valid });
            
            // Send soft_signup update with current conversion state
            const merged = mergeAnswerObjects(answers, { [currentStep.answerKey]: payload });
            sendSoftSignupUpdate(merged);

            // Handle manual contact submission - check if user exists before collecting DOB
            if (currentStep.id === STEP_IDS.GLOBAL_MANUAL_CONTACT) {
              // Get user data from manual contact
              const name = payload?.name || "";
              const email = payload?.email || "";
              const phone = payload?.phone || "";
              
              // Validate full name - must have at least first name and last name
              if (!name || name.trim().length === 0) {
                push({ role: "system", text: t("manual.fullNameRequired"), id: `full-name-required-${Date.now()}` });
                return;
              }
              const nameParts = name.trim().split(/\s+/).filter(part => part.length > 0);
              if (nameParts.length < 2) {
                push({ role: "system", text: t("manual.fullNameRequired"), id: `full-name-required-${Date.now()}` });
                return;
              }
              
              // Send contact info to API with user's path
              sendContactInfoToAPI({ name, email, phone }, answers, currentStep.id);
              
              // Fire Meta Pixel lead event
              if (!leadEventFiredRef.current) {
                const mergedAnswers = mergeAnswerObjects(answers, { [currentStep.answerKey]: payload });
                const eventName = getLeadEventName(mergedAnswers);
                if (eventName) {
                  trackLeadEvent(eventName, {
                    stepId: currentStep.id,
                    persona: mergedAnswers?.persona,
                    renterReasons: mergedAnswers?.renterReasons,
                  });
                  leadEventFiredRef.current = true;
                }
              }
              
              // For agent persona, skip directSignupCustomer and just advance to completion
              if (answers?.persona === "agent") {
                setIsSubmittingContact(true);
                const merged = mergeAnswerObjects(answers, { [currentStep.answerKey]: payload });
                setAnswers(merged);
                LocalStorage.write("welcome_flow_answers", merged);
                // Advance to next step (should be AGENT_CONTACT_COMPLETE)
                setTimeout(() => {
                  setIsSubmittingContact(false);
                  resolveNext({ [currentStep.answerKey]: payload });
                }, 0);
                return;
              }
              
              // For owner/operator personas (owner, mom_and_pop, large_owner), skip directSignupCustomer and 2FA
              if (answers?.persona === "owner" || answers?.persona === "mom_and_pop" || answers?.persona === "large_owner") {
                setIsSubmittingContact(true);
                const merged = mergeAnswerObjects(answers, { [currentStep.answerKey]: payload });
                setAnswers(merged);
                LocalStorage.write("welcome_flow_answers", merged);
                // Show "we'll be in touch" message and advance to end
                push({ role: "system", text: t("owner.contactComplete"), id: `owner-contact-complete-${Date.now()}` });
                setTimeout(() => {
                  setIsSubmittingContact(false);
                  resolveNext({ [currentStep.answerKey]: payload });
                }, 0);
                return;
              }
              
              // Set loading state to show dots and hide fields (echo already pushed above)
              setIsSubmittingContact(true);
              
              if (name && email && phone) {
                // Split name into first_name and last_name
                const nameParts = name.trim().split(/\s+/).filter(part => part.length > 0);
                const first_name = nameParts[0] || "";
                const last_name = nameParts.slice(1).join(" ") || "";
                
                // Use placeholder DOB for initial check (will collect real DOB after if new user)
                const placeholderDOB = "1900-01-01";
                
                    setIsSubmittingContact(true);
                    
                    // Check if user exists by calling API with placeholder DOB
                directSignupCustomer(
                  {
                    user: {
                      first_name: first_name.trim(),
                      last_name: last_name.trim(),
                      email: email.trim().toLowerCase(),
                      birthdate: placeholderDOB,
                      phone: phone,
                    },
                    agent_id: LocalStorage.read(appUtilityKeys.agentId) || null,
                  },
                  {
                    onSuccess: () => {
                      // New user created with placeholder DOB - request 2FA code before account explainer
                      // Set flag to track that we created this user, so we can handle 409 differently on DOB submission
                      const mergedWithFlag = mergeAnswerObjects(answers, { 
                        [currentStep.answerKey]: payload, 
                        userCreatedWithPlaceholderDOB: true 
                      });
                      setAnswers(mergedWithFlag);
                      LocalStorage.write("welcome_flow_answers", mergedWithFlag);
                      // Request 2FA code for new user before account explainer
                      requestLogin2FACode(
                        { 
                          phoneNumber: transformUSNumberToCountryCodeFormat(phone),
                          ...debugProps
                        },
                        {
                          onSuccess: () => {
                            // Advance to 2FA verification step
                            setCurrentStepId(STEP_IDS.CASHBACK_2FA_REQUEST);
                            // Keep isSubmittingContact true until step actually changes
                            setTimeout(() => setIsSubmittingContact(false), 0);
                          },
                          onError: (err2) => {
                            push({ role: "system", text: err2?.response?.data?.error || t("bank.2FARequestError"), id: `2fa-request-error-${Date.now()}` });
                            // Still advance even if 2FA request fails
                            setCurrentStepId(STEP_IDS.CASHBACK_2FA_REQUEST);
                            // Keep isSubmittingContact true until step actually changes
                            setTimeout(() => setIsSubmittingContact(false), 0);
                          }
                        }
                      );
                    },
                    onError: (err) => {
                      const statusCode = err?.response?.status;
                          if (statusCode === 409) {
                            // User already exists - route to returning user flow (2FA login)
                            push({ role: "system", text: t("bank.accountFound"), id: `account-found-${Date.now()}` });
                            // Set userExists flag for routing
                            const mergedWithFlag = mergeAnswerObjects(answers, { [currentStep.answerKey]: payload, userExists: true });
                            setAnswers(mergedWithFlag);
                            LocalStorage.write("welcome_flow_answers", mergedWithFlag);
                            // Request 2FA code for returning user
                            requestLogin2FACode(
                              { 
                                phoneNumber: transformUSNumberToCountryCodeFormat(phone),
                                ...debugProps
                              },
                              {
                                onSuccess: () => {
                                  // Advance to 2FA verification step for returning user
                                  setCurrentStepId(STEP_IDS.CASHBACK_2FA_REQUEST);
                                  // Keep isSubmittingContact true until step actually changes
                                  setTimeout(() => setIsSubmittingContact(false), 0);
                                },
                                onError: (err2) => {
                                  push({ role: "system", text: err2?.response?.data?.error || t("bank.2FARequestError"), id: `2fa-request-error-${Date.now()}` });
                                  // Still advance even if 2FA request fails
                                  setCurrentStepId(STEP_IDS.CASHBACK_2FA_REQUEST);
                                  // Keep isSubmittingContact true until step actually changes
                                  setTimeout(() => setIsSubmittingContact(false), 0);
                                }
                              }
                            );
                      } else {
                        setIsSubmittingContact(false);
                        // Other errors - show error message but still continue with new user flow
                        const errorMessage = err?.response?.data?.error || err?.response?.data?.msg || t("bank.signupError");
                        push({ role: "system", text: errorMessage, id: `signup-error-${Date.now()}` });
                        // Continue to DOB collection for new user flow
                        resolveNext({ [currentStep.answerKey]: payload });
                      }
                    }
                  }
                );
                return; // Don't call resolveNext again, handled in callbacks
              }
            }

            // Handle DOB submission - update user with real DOB if they were created with placeholder DOB
            if (currentStep.id === STEP_IDS.GLOBAL_DOB_CHECK || currentStep.id === STEP_IDS.CASHBACK_BANK_DOB) {
              const mergedAnswers = mergeAnswerObjects(answers, { [currentStep.answerKey]: payload });
              
              // Check if user was already identified as existing (from manual contact or phone check)
              if (mergedAnswers.userExists) {
                // User already identified as existing - should have been routed to 2FA, but handle gracefully
                resolveNext({ [currentStep.answerKey]: payload });
                return;
              }
              
              // Check if user was created with placeholder DOB (from manual contact or phone check)
              // If so, update with real DOB; otherwise this is a fallback check for edge cases
              setIsSubmittingDOB(true);
              
              // Get user data from manual contact (if available) or Google profile
              let name = "";
              let email = "";
              let phone = "";
              
              // First, check if we have manual contact info in answers
              const manualContact = mergedAnswers.manualContact || {};
              if (manualContact.name && manualContact.email && manualContact.phone) {
                // Use manual contact info if available
                name = manualContact.name || "";
                email = manualContact.email || "";
                phone = manualContact.phone || "";
              } else {
                // Fallback to Google profile stored in localStorage
                try {
                  const raw = (typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("stake_ai_soft_signup")) || null;
                  const stored = raw ? JSON.parse(raw) : {};
                  const profile = stored?.profile || {};
                  const profName = profile.name || "";
                  const profGiven = profile.given_name || profile.givenName || "";
                  const profFamily = profile.family_name || profile.familyName || "";
                  const profEmail = profile.email || "";
                  
                  // Get phone from answers (could be from cashbackPhoneDetails or other sources)
                  const phoneDetails = mergedAnswers.phoneCallDetails || mergedAnswers.phoneTextDetails || mergedAnswers.cashbackPhoneDetails || {};
                  phone = phoneDetails.phone || "";
                  
                  // Construct name from Google profile
                  if (profName) {
                    name = profName;
                  } else if (profGiven) {
                    name = profFamily ? `${profGiven} ${profFamily}` : profGiven;
                  }
                  
                  email = profEmail;
                } catch (e) {
                  console.error("Error reading Google profile from localStorage", e);
                }
              }
              
              const dob = payload?.dob || "";
              
              if (name && email && phone && dob) {
                // Split name into first_name and last_name
                const nameParts = name.trim().split(/\s+/);
                const first_name = nameParts[0] || "";
                const last_name = nameParts.slice(1).join(" ") || "";
                
                // Format payload to match backend API expectations
                // Backend expects: { user: { first_name, last_name, email, birthdate, phone }, agent_id? }
                directSignupCustomer(
                  {
                    user: {
                      first_name: first_name.trim(),
                      last_name: last_name.trim(),
                      email: email.trim().toLowerCase(),
                      birthdate: dob, // Already in YYYY-MM-DD format from date picker
                      phone: phone,
                    },
                    agent_id: LocalStorage.read(appUtilityKeys.agentId) || null,
                  },
                  {
                    onSuccess: () => {
                      // New user created (or updated with real DOB if previously created with placeholder)
                      // Route to occupation for cashback flows (account explainer already shown before DOB)
                      setIsSubmittingDOB(false);
                      const reasons = answers?.renterReasons ?? [];
                      if (reasons.includes("cashback")) {
                        // For cashback flows, go to occupation after DOB
                        setCurrentStepId(STEP_IDS.CASHBACK_BANK_OCCUPATION);
                      } else {
                      resolveNext({ [currentStep.answerKey]: payload });
                      }
                    },
                    onError: (err) => {
                      const statusCode = err?.response?.status;
                      if (statusCode === 409) {
                        // Check if we created this user with placeholder DOB - if so, continue flow (user was just created)
                        // Otherwise, user already existed - route to returning user flow (2FA login)
                        if (mergedAnswers.userCreatedWithPlaceholderDOB) {
                          // We just created this user with placeholder DOB, so 409 means user exists (we created them)
                          // Continue with the flow - the API may have updated the DOB or we'll handle it
                          setIsSubmittingDOB(false);
                          const reasons = answers?.renterReasons ?? [];
                          if (reasons.includes("cashback")) {
                            setCurrentStepId(STEP_IDS.CASHBACK_BANK_OCCUPATION);
                          } else {
                            resolveNext({ [currentStep.answerKey]: payload });
                          }
                        } else {
                          // User already existed before - route to returning user flow (2FA login)
                        setIsSubmittingDOB(false);
                        push({ role: "system", text: t("bank.accountFound"), id: `account-found-${Date.now()}` });
                        // Set userExists flag for routing
                        const mergedWithFlag = mergeAnswerObjects(answers, { [currentStep.answerKey]: payload, userExists: true });
                        setAnswers(mergedWithFlag);
                        LocalStorage.write("welcome_flow_answers", mergedWithFlag);
                        // Request 2FA code for returning user
                        requestLogin2FACode(
                          { 
                            phoneNumber: transformUSNumberToCountryCodeFormat(phone),
                            ...debugProps
                          },
                          {
                            onSuccess: () => {
                              // Advance to 2FA verification step for returning user
                              setCurrentStepId(STEP_IDS.CASHBACK_2FA_REQUEST);
                            },
                            onError: (err2) => {
                              push({ role: "system", text: err2?.response?.data?.error || t("bank.2FARequestError"), id: `2fa-request-error-${Date.now()}` });
                              // Still advance even if 2FA request fails
                              setCurrentStepId(STEP_IDS.CASHBACK_2FA_REQUEST);
                            }
                          }
                        );
                        }
                      } else {
                        setIsSubmittingDOB(false);
                        // Other errors - show error message but still continue with new user flow
                        const errorMessage = err?.response?.data?.error || err?.response?.data?.msg || t("bank.signupError");
                        push({ role: "system", text: errorMessage, id: `signup-error-${Date.now()}` });
                        // For cashback flows, route to occupation; otherwise continue with occupation
                        const reasons = answers?.renterReasons ?? [];
                        if (reasons.includes("cashback")) {
                          setCurrentStepId(STEP_IDS.CASHBACK_BANK_OCCUPATION);
                        } else {
                        resolveNext({ [currentStep.answerKey]: payload });
                        }
                      }
                    }
                  }
                );
                return; // Don't call resolveNext again
              } else {
                setIsSubmittingDOB(false);
                // Missing required fields - show error
                push({ role: "system", text: t("bank.missingContactInfo"), id: `missing-info-${Date.now()}` });
              }
            }

            if (
              currentStep.id === STEP_IDS.RENTER_PHONE_CALL ||
              currentStep.id === STEP_IDS.RENTER_PHONE_TEXT ||
              currentStep.id === STEP_IDS.CASHBACK_PHONE_INPUT
            ) {
              const conversionState = { ...answers, [currentStep.answerKey]: payload };
              // Use utility function for consistency
              sendSoftSignupUpdate(conversionState);
              
              // Merge answers for use in tracking and API calls
              const mergedAnswers = mergeAnswerObjects(answers, { [currentStep.answerKey]: payload });
              
              // Fire Meta Pixel lead event
              if (!leadEventFiredRef.current) {
                const eventName = getLeadEventName(mergedAnswers);
                if (eventName) {
                  trackLeadEvent(eventName, {
                    stepId: currentStep.id,
                    persona: mergedAnswers?.persona,
                    renterReasons: mergedAnswers?.renterReasons,
                  });
                  leadEventFiredRef.current = true;
                }
              }
              
              // Send contact info to API with user's path
              const phone = payload?.phone || "";
              // Try to get name and email from answers or localStorage
              let name = "";
              let email = "";
              const manualContact = mergedAnswers.manualContact || {};
              if (manualContact.name) name = manualContact.name;
              if (manualContact.email) email = manualContact.email;
              
              // Also check localStorage for Google profile
              try {
                const raw = (typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("stake_ai_soft_signup")) || null;
                const stored = raw ? JSON.parse(raw) : {};
                const profile = stored?.profile || {};
                if (!name && profile.name) name = profile.name;
                if (!name && (profile.given_name || profile.givenName)) {
                  name = profile.family_name || profile.familyName 
                    ? `${profile.given_name || profile.givenName} ${profile.family_name || profile.familyName}`
                    : profile.given_name || profile.givenName;
                }
                if (!email && profile.email) email = profile.email;
              } catch (e) {
                console.error("Error reading profile from localStorage", e);
              }
              
              sendContactInfoToAPI({ name, email, phone }, mergedAnswers, currentStep.id);
              
              if (currentStep.id === STEP_IDS.CASHBACK_PHONE_INPUT) {
                // For Google sign-in users, check if user exists with placeholder DOB after phone collection
                if (phone) {
                  // Get user data from Google profile stored in localStorage
                  let googleName = name || "";
                  let googleEmail = email || "";
                  
                  try {
                    const raw = (typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("stake_ai_soft_signup")) || null;
                    const stored = raw ? JSON.parse(raw) : {};
                    const profile = stored?.profile || {};
                    const profName = profile.name || "";
                    const profGiven = profile.given_name || profile.givenName || "";
                    const profFamily = profile.family_name || profile.familyName || "";
                    const profEmail = profile.email || "";
                    
                    // Construct name from Google profile
                    if (!googleName && profName) {
                      googleName = profName;
                    } else if (!googleName && profGiven) {
                      googleName = profFamily ? `${profGiven} ${profFamily}` : profGiven;
                    }
                    if (!googleEmail && profEmail) {
                      googleEmail = profEmail;
                    }
                  } catch (e) {
                    console.error("Error reading Google profile from localStorage", e);
                  }
                  
                  // Use the name and email we already extracted above
                  const finalName = googleName || name;
                  const finalEmail = googleEmail || email;
                  
                  if (finalName && finalEmail && phone) {
                    // Split name into first_name and last_name
                    const nameParts = (finalName || name).trim().split(/\s+/);
                    const first_name = nameParts[0] || "";
                    const last_name = nameParts.slice(1).join(" ") || "";
                    
                    // Use placeholder DOB for initial check (will collect real DOB after if new user)
                    const placeholderDOB = "1900-01-01";
                    
                    setIsSubmittingContact(true);
                    
                    // Check if user exists by calling API with placeholder DOB
                    directSignupCustomer(
                      {
                        user: {
                          first_name: first_name.trim(),
                          last_name: last_name.trim(),
                          email: (finalEmail || email).trim().toLowerCase(),
                          birthdate: placeholderDOB,
                          phone: phone,
                        },
                        agent_id: LocalStorage.read(appUtilityKeys.agentId) || null,
                      },
                      {
                        onSuccess: () => {
                          // New user created with placeholder DOB - request 2FA code before account explainer
                          // Set flag to track that we created this user, so we can handle 409 differently on DOB submission
                          const mergedWithFlag = mergeAnswerObjects(answers, { 
                            [currentStep.answerKey]: payload, 
                            userCreatedWithPlaceholderDOB: true 
                          });
                          setAnswers(mergedWithFlag);
                          LocalStorage.write("welcome_flow_answers", mergedWithFlag);
                          // Request 2FA code for new user before account explainer
                          requestLogin2FACode(
                            { 
                              phoneNumber: transformUSNumberToCountryCodeFormat(phone),
                              ...debugProps
                            },
                            {
                              onSuccess: () => {
                                // Advance to 2FA verification step
                                setCurrentStepId(STEP_IDS.CASHBACK_2FA_REQUEST);
                                // Keep isSubmittingContact true until step actually changes
                                setTimeout(() => setIsSubmittingContact(false), 0);
                              },
                              onError: (err2) => {
                                push({ role: "system", text: err2?.response?.data?.error || t("bank.2FARequestError"), id: `2fa-request-error-${Date.now()}` });
                                // Still advance even if 2FA request fails
                                setCurrentStepId(STEP_IDS.CASHBACK_2FA_REQUEST);
                                // Keep isSubmittingContact true until step actually changes
                                setTimeout(() => setIsSubmittingContact(false), 0);
                              }
                            }
                          );
                        },
                        onError: (err) => {
                          const statusCode = err?.response?.status;
                          if (statusCode === 409) {
                            // User already exists - route to returning user flow (2FA login)
                            push({ role: "system", text: t("bank.accountFound"), id: `account-found-${Date.now()}` });
                            // Set userExists flag for routing
                            const mergedWithFlag = mergeAnswerObjects(answers, { [currentStep.answerKey]: payload, userExists: true });
                            setAnswers(mergedWithFlag);
                            LocalStorage.write("welcome_flow_answers", mergedWithFlag);
                            // Request 2FA code for returning user
                            requestLogin2FACode(
                              { 
                                phoneNumber: transformUSNumberToCountryCodeFormat(phone),
                                ...debugProps
                              },
                              {
                                onSuccess: () => {
                                  // Advance to 2FA verification step for returning user
                                  setCurrentStepId(STEP_IDS.CASHBACK_2FA_REQUEST);
                                  // Keep isSubmittingContact true until step actually changes
                                  setTimeout(() => setIsSubmittingContact(false), 0);
                                },
                                onError: (err2) => {
                                  push({ role: "system", text: err2?.response?.data?.error || t("bank.2FARequestError"), id: `2fa-request-error-${Date.now()}` });
                                  // Still advance even if 2FA request fails
                                  setCurrentStepId(STEP_IDS.CASHBACK_2FA_REQUEST);
                                  // Keep isSubmittingContact true until step actually changes
                                  setTimeout(() => setIsSubmittingContact(false), 0);
                                }
                              }
                            );
                          } else {
                            setIsSubmittingDOB(false);
                            // Other errors - show error message but still continue with new user flow
                            const errorMessage = err?.response?.data?.error || err?.response?.data?.msg || t("bank.signupError");
                            push({ role: "system", text: errorMessage, id: `signup-error-${Date.now()}` });
                            // Continue to DOB collection for new user flow
                            resolveNext({ [currentStep.answerKey]: payload });
                          }
                        }
                      }
                    );
                    return; // Don't call resolveNext again, handled in callbacks
                  }
                }
                // Don't push phone thanks here, it will be shown by the cashbackPhoneThanks step
              } else if (currentStep.id === STEP_IDS.RENTER_PHONE_CALL) {
                push({ role: "system", text: t("findHome.phoneCallThanks"), id: `phone-call-thanks-${Date.now()}` });
              } else {
                push({ role: "system", text: t("findHome.phoneThanks"), id: `phone-thanks-${Date.now()}` });
              }
            }
            
            // Handle email submission for find home flow
            if (currentStep.id === STEP_IDS.RENTER_EMAIL) {
              const email = payload?.email || "";
              const mergedAnswers = mergeAnswerObjects(answers, { [currentStep.answerKey]: payload });
              // Try to get name and phone from answers or localStorage
              let name = "";
              let phone = "";
              const manualContact = mergedAnswers.manualContact || {};
              if (manualContact.name) name = manualContact.name;
              if (manualContact.phone) phone = manualContact.phone;
              
              // Also check localStorage for Google profile
              try {
                const raw = (typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("stake_ai_soft_signup")) || null;
                const stored = raw ? JSON.parse(raw) : {};
                const profile = stored?.profile || {};
                if (!name && profile.name) name = profile.name;
                if (!name && (profile.given_name || profile.givenName)) {
                  name = profile.family_name || profile.familyName 
                    ? `${profile.given_name || profile.givenName} ${profile.family_name || profile.familyName}`
                    : profile.given_name || profile.givenName;
                }
                // Phone might be in phoneCallDetails or phoneTextDetails
                const phoneDetails = mergedAnswers.phoneCallDetails || mergedAnswers.phoneTextDetails || {};
                if (!phone && phoneDetails.phone) phone = phoneDetails.phone;
              } catch (e) {
                console.error("Error reading profile from localStorage", e);
              }
              
              sendContactInfoToAPI({ name, email, phone }, mergedAnswers, currentStep.id);
            }

            // Validate address step - require all address fields and valid USA address from Google autocomplete
            if (currentStep.id === STEP_IDS.CASHBACK_BANK_ADDRESS) {
              const address = payload?.address || "";
              const city = payload?.city || "";
              const state = payload?.state || "";
              const zipCode = payload?.zipCode || "";
              
              if (!address || !city || !state || !zipCode) {
                push({ role: "system", text: t("bank.incompleteAddress"), id: `incomplete-address-${Date.now()}` });
                return;
              }
              
              // Check if address was selected from Google autocomplete (has all components)
              // If user typed manually, these fields won't be properly populated
              if (!payload?.addressSelectedFromAutocomplete) {
                push({ role: "system", text: t("bank.addressMustBeSelected"), id: `address-not-selected-${Date.now()}` });
                return;
              }
            }

            // Validate DOB step - require date of birth, no future dates, no dates before 1900, must be 18+
            if (currentStep.id === STEP_IDS.GLOBAL_DOB_CHECK || currentStep.id === STEP_IDS.CASHBACK_BANK_DOB) {
              const dob = payload?.dob || "";
              
              if (!dob || dob.trim().length === 0) {
                push({ role: "system", text: t("bank.dobRequired"), id: `dob-required-${Date.now()}` });
                return;
              }
              
              const dobMoment = moment(dob);
              const today = moment().startOf('day');
              const year1900 = moment('1900-01-01');
              const eighteenYearsAgo = moment().subtract(18, 'years').startOf('day');
              
              if (dobMoment.isAfter(today)) {
                push({ role: "system", text: t("bank.dobFutureDate"), id: `dob-future-${Date.now()}` });
                return;
              }
              
              if (dobMoment.isBefore(year1900)) {
                push({ role: "system", text: t("bank.dobBefore1900"), id: `dob-before-1900-${Date.now()}` });
                return;
              }
              
              if (dobMoment.isAfter(eighteenYearsAgo)) {
                push({ role: "system", text: t("bank.dobUnder18"), id: `dob-under-18-${Date.now()}` });
                return;
              }
            }

            // Validate move-in date step - require date, no past dates
            if (currentStep.id === STEP_IDS.RENTER_MOVE_IN) {
              const moveInDate = payload?.moveInDate || "";
              
              if (!moveInDate || moveInDate.trim().length === 0) {
                push({ role: "system", text: t("findHome.moveInDateRequired"), id: `move-in-required-${Date.now()}` });
                return;
              }
              
              const moveInMoment = moment(moveInDate);
              const today = moment().startOf('day');
              
              if (moveInMoment.isBefore(today)) {
                push({ role: "system", text: t("findHome.moveInDatePast"), id: `move-in-past-${Date.now()}` });
                return;
              }
            }

            // Validate SSN step - require 9 digits
            if (currentStep.id === STEP_IDS.CASHBACK_BANK_SSN) {
              const ssn = payload?.ssn || "";
              const ssnDigits = ssn.replace(/\D/g, "");
              
              if (!ssn || ssnDigits.length !== 9) {
                push({ role: "system", text: t("bank.ssnRequired"), id: `ssn-required-${Date.now()}` });
                return;
              }
            }

            // Handle 2FA verification for cashback flow
            if (currentStep.id === STEP_IDS.CASHBACK_2FA_VERIFY) {
              const code = payload?.code || "";
              if (!code || code.trim().length === 0) {
                push({ role: "system", text: t("bank.2FACodeRequired"), id: `2fa-code-required-${Date.now()}` });
                return;
              }
              
              // Use the same phone retrieval logic as in DOB step to ensure consistency
              const manualContact = answers.manualContact || {};
              const phoneDetails = answers.phoneCallDetails || answers.phoneTextDetails || answers.cashbackPhoneDetails || {};
              const phone = manualContact.phone || phoneDetails.phone || "";
              
              if (!phone) {
                push({ role: "system", text: t("bank.missingPhone"), id: `missing-phone-${Date.now()}` });
                return;
              }
              
              // Format phone number for 2FA verification
              const formattedPhone = transformUSNumberToCountryCodeFormat(phone);
              const loginPayload = { 
                phoneNumber: formattedPhone, 
                phoneNumberCode: code.trim(),
                ...debugProps
              };
              
              console.log("2FA login payload:", loginPayload);
              console.log("Phone from answers:", phone);
              console.log("Formatted phone:", formattedPhone);
              console.log("Code:", code.trim());
              
              // Verify 2FA code (phone needs to be formatted)
              // Don't add code to messages yet - only add on success
              loginWith2FACode(
                loginPayload,
                {
                  onSuccess: (response) => {
                    // Add code as user message only on success
                    const echo = formatEchoPayload(currentStep.id, payload);
                    if (echo) push({ role: "user", text: echo, id: `${currentStep.id}-echo` });
                    
                    // Update user credentials from login response
                    onUserCredentialsUpdate(response);
                    // Clear error message ID ref on success
                    last2FAErrorIdRef.current = null;
                    // For cashback flows, route to account explainer after 2FA verification
                    const reasons = answers?.renterReasons ?? [];
                    if (reasons.includes("cashback")) {
                      setCurrentStepId(STEP_IDS.CASHBACK_ACCOUNT_EXPLAINER);
                    } else {
                      resolveNext({ [currentStep.answerKey]: payload });
                    }
                  },
                  onError: (err) => {
                    // Clear the input field on error
                    setAnswers((a) =>
                      mergeAnswerObjects(a, {
                        [currentStep.answerKey]: { ...(a[currentStep.answerKey] || {}), code: "" }
                      })
                    );
                    
                    // Replace existing error message instead of adding a new one
                    const errorId = `2fa-verify-error-${Date.now()}`;
                    if (last2FAErrorIdRef.current) {
                      // Remove the previous error message
                      setMessages((msgs) => msgs.filter((msg) => msg.id !== last2FAErrorIdRef.current));
                    }
                    // Add new error message
                    push({ role: "system", text: t("bank.2FAVerifyError"), id: errorId });
                    last2FAErrorIdRef.current = errorId;
                  }
                }
              );
              return;
            }

            resolveNext({ [currentStep.answerKey]: payload });
          };
          return (
            <TextStep
              ref={controlsRef}
              step={currentStep}
              t={t}
              answers={answers}
              setAnswers={setAnswers}
              onSubmit={handleAdvanceTextStep}
              onShowPiiNotice={markPiiConsentAcknowledged}
              isSubmitting={
                (currentStep.id === STEP_IDS.GLOBAL_DOB_CHECK || currentStep.id === STEP_IDS.CASHBACK_BANK_DOB) && isSubmittingDOB ||
                (currentStep.id === STEP_IDS.GLOBAL_MANUAL_CONTACT || currentStep.id === STEP_IDS.CASHBACK_PHONE_INPUT) && isSubmittingContact
              }
            />
          );
        }

        return null;
      })()}

      <div ref={bottomRef} style={{ paddingBottom: 40 }} />
    </>
  );
}

const SingleChoiceStep = forwardRef(function SingleChoiceStep(
  {
    step,
    t,
    onChooseSingle,
    awaitingOther,
    singleOtherValue,
    setSingleOtherValue,
    onSubmitOther,
    singleSelectValue,
    setSingleSelectValue,
    showPiiNotice,
    piiConsentAcknowledged,
    onShowPiiNotice
  },
  ref
) {
  // Ensure options is an array (should already be resolved, but add safety check)
  const options = Array.isArray(step.options) ? step.options : [];
  const otherOption = options.find((o) => o.id === "other");
  useEffect(() => {
    if (showPiiNotice && !piiConsentAcknowledged) onShowPiiNotice?.();
  }, [showPiiNotice, piiConsentAcknowledged, onShowPiiNotice]);
  // Always show PII notice at GLOBAL_IDENTITY and RENTER_CONTACT_METHOD steps, even if already acknowledged
  const shouldShowPiiNotice = showPiiNotice && (step.id === STEP_IDS.GLOBAL_IDENTITY || step.id === STEP_IDS.RENTER_CONTACT_METHOD || !piiConsentAcknowledged);
  if (step.id === STEP_IDS.CASHBACK_BANK_OCCUPATION) {
    const selectOptions = options.map((opt) => ({
      label: t(opt.labelKey),
      value: opt.id
    }));
    return (
      <div ref={ref} className="stake-ai-controls">
        <div className="stake-ai-form">
          <SelectField
            label={t(step.promptKey)}
            options={selectOptions}
            value={singleSelectValue}
            onChange={(e) => {
              const val = e.target.value;
              setSingleSelectValue(val);
              const opt = options.find((o) => o.id === val);
              if (opt) onChooseSingle(opt);
            }}
            dropUp
            className="filled"
            fullWidth
          />
        </div>
      </div>
    );
  }
  return (
    <div ref={ref} className="stake-ai-controls">
      {shouldShowPiiNotice && <PIIConsentNotice />}
      {/* Agreements are now shown in the message bubble, so hide them here */}
      <div className="stake-ai-options">
        {options.map((opt) => (
          <div key={opt.id} style={{ width: "100%" }}>
            <OutlineButton onClick={() => onChooseSingle(opt)} style={{ width: "100%", textAlign: "left", flexDirection: "column", alignItems: "flex-start" }}>
              <span>{opt.labelKey ? t(opt.labelKey) : opt.label}</span>
              {opt.helperTextKey && (
                <span style={{ fontSize: "12px", opacity: 0.8, marginTop: "4px", fontWeight: "normal" }}>
                  {t(opt.helperTextKey)}
                </span>
              )}
            </OutlineButton>
          </div>
        ))}
      </div>
      {awaitingOther && otherOption && (
        <div className="stake-ai-actions" style={{ marginTop: 12 }}>
          <input
            className="stake-ai-input"
            placeholder={otherOption.otherPlaceholderKey ? t(otherOption.otherPlaceholderKey) : ""}
            value={singleOtherValue}
            onChange={(e) => setSingleOtherValue(e.target.value)}
            aria-label={otherOption.otherPlaceholderKey ? t(otherOption.otherPlaceholderKey) : "Please specify"}
          />
          <div className="stake-ai-actions" style={{ marginTop: 8 }}>
            <PrimaryButton onClick={onSubmitOther}>{t("manual.continue")}</PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
});

const SliderStep = forwardRef(function SliderStep({ step, t, value, formatted, onSliderChange, onSubmit }, ref) {
  const sliderId = `slider-${step.id || "value"}`;
  return (
    <div ref={ref} className="stake-ai-controls">
      <div className="stake-ai-form">
        <label htmlFor={sliderId} className="text-sm-medium color--mist" style={{ fontSize: "24px", fontWeight: "600", marginBottom: "16px", display: "block" }}>
          {formatted}
        </label>
        <input
          id={sliderId}
          type="range"
          min={step.slider?.min ?? 0}
          max={step.slider?.max ?? 100}
          step={step.slider?.step ?? 1}
          value={value}
          onChange={(e) => onSliderChange(e.target.value)}
          className="stake-ai-input"
          aria-valuemin={step.slider?.min ?? 0}
          aria-valuemax={step.slider?.max ?? 100}
          aria-valuenow={value}
          aria-valuetext={formatted}
        />
      </div>
      <div className="stake-ai-actions">
        <PrimaryButton onClick={onSubmit}>{t("manual.continue")}</PrimaryButton>
      </div>
    </div>
  );
});

const MultiChoiceStep = forwardRef(function MultiChoiceStep(
  { step, t, selections, toggleSelection, otherValue, setOtherValue, onSubmit },
  ref
) {
  // Ensure options is an array (should already be resolved, but add safety check)
  const options = Array.isArray(step.options) ? step.options : [];
  const otherOption = options.find((o) => o.id === "other");
  return (
    <div ref={ref} className="stake-ai-controls">
      <div className="stake-ai-options stake-ai-options-grid">
        {options.map((opt) => {
          const isSelected = selections.includes(opt.id);
          return (
            <OutlineButton
              key={opt.id}
              onClick={() => toggleSelection(opt.id)}
              className={isSelected ? "selected" : ""}
            >
              {opt.labelKey ? t(opt.labelKey) : opt.label}
            </OutlineButton>
          );
        })}
      </div>
      {selections.includes("other") && (
        <div style={{ marginTop: 12 }}>
          <input
            className="stake-ai-input"
            placeholder={otherOption?.otherPlaceholderKey ? t(otherOption.otherPlaceholderKey) : ""}
            value={otherValue}
            onChange={(e) => setOtherValue(e.target.value)}
            aria-label={otherOption?.otherPlaceholderKey ? t(otherOption.otherPlaceholderKey) : "Please specify"}
          />
        </div>
      )}
      <div className="stake-ai-actions">
        <PrimaryButton onClick={onSubmit}>{t("manual.continue")}</PrimaryButton>
      </div>
    </div>
  );
});

const TextStep = forwardRef(function TextStep({ step, t, answers, setAnswers, onSubmit, onShowPiiNotice, isSubmitting }, ref) {
  const payload = answers?.[step.answerKey] || {};
  const shouldShowNotice = step.showPiiNotice && !answers?.piiConsentAcknowledged;
  const [datePickerOpenStates, setDatePickerOpenStates] = useState({});
  useEffect(() => {
    if (shouldShowNotice) onShowPiiNotice?.();
  }, [shouldShowNotice, onShowPiiNotice]);
  
  // Check if this is a contact submission step that's currently submitting
  const isContactStep = step.id === STEP_IDS.GLOBAL_MANUAL_CONTACT || step.id === STEP_IDS.CASHBACK_PHONE_INPUT;
  const isSubmittingContact = isContactStep && isSubmitting;
  
  // Check if required fields are valid for each step
  const isAddressStep = step.id === STEP_IDS.CASHBACK_BANK_ADDRESS;
  const isDOBStep = step.id === STEP_IDS.GLOBAL_DOB_CHECK || step.id === STEP_IDS.CASHBACK_BANK_DOB;
  const isSSNStep = step.id === STEP_IDS.CASHBACK_BANK_SSN;
  const is2FAStep = step.id === STEP_IDS.CASHBACK_2FA_VERIFY;
  
  let isStepValid = true;
  if (isAddressStep) {
    isStepValid = !!(payload?.address && payload?.city && payload?.state && payload?.zipCode && payload?.addressSelectedFromAutocomplete);
  } else if (isDOBStep) {
    const dob = payload?.dob || "";
    if (!dob || dob.trim().length === 0) {
      isStepValid = false;
    } else {
      const dobMoment = moment(dob);
      const today = moment().startOf('day');
      const year1900 = moment('1900-01-01');
      const eighteenYearsAgo = moment().subtract(18, 'years').startOf('day');
      isStepValid = !dobMoment.isAfter(today) && !dobMoment.isBefore(year1900) && !dobMoment.isAfter(eighteenYearsAgo);
    }
  } else if (step.id === STEP_IDS.RENTER_MOVE_IN) {
    const moveInDate = payload?.moveInDate || "";
    if (!moveInDate || moveInDate.trim().length === 0) {
      isStepValid = false;
    } else {
      const moveInMoment = moment(moveInDate);
      const today = moment().startOf('day');
      isStepValid = !moveInMoment.isBefore(today);
    }
  } else if (isSSNStep) {
    const ssnDigits = (payload?.ssn || "").replace(/\D/g, "");
    isStepValid = ssnDigits.length === 9;
  } else if (step.id === STEP_IDS.CASHBACK_PHONE_INPUT) {
    const phoneDigits = (payload?.phone || "").replace(/\D/g, "");
    isStepValid = phoneDigits.length === 10;
  } else if (step.id === STEP_IDS.RENTER_PHONE_TEXT || step.id === STEP_IDS.RENTER_PHONE_CALL) {
    const phoneDigits = (payload?.phone || "").replace(/\D/g, "");
    const hasValidPhone = phoneDigits.length === 10;
    // Check if consent is required and if it's been given
    const hasConsent = step.requireConsent ? payload?.consent === true : true;
    isStepValid = hasValidPhone && hasConsent;
  } else if (is2FAStep) {
    const code = payload?.code || "";
    isStepValid = !!(code && code.trim().length > 0);
  } else if (step.id === STEP_IDS.RENTER_NAME_EMAIL) {
    const name = payload?.name || "";
    const email = payload?.email || "";
    // Name must have at least first and last name
    const nameParts = name.trim().split(/\s+/).filter(part => part.length > 0);
    const hasValidName = name && name.trim().length > 0 && nameParts.length >= 2;
    const hasValidEmail = email && validation.email.isAValidEmail(email.trim());
    isStepValid = hasValidName && hasValidEmail;
  } else if (step.id === STEP_IDS.GLOBAL_MANUAL_CONTACT) {
    const name = payload?.name || "";
    const email = payload?.email || "";
    const phone = payload?.phone || "";
    // Name must have at least first and last name
    const nameParts = name.trim().split(/\s+/).filter(part => part.length > 0);
    isStepValid = !!(name && name.trim().length > 0 && nameParts.length >= 2 && email && phone);
  }
  const updateField = (fieldId, value) => {
    setAnswers((a) =>
      mergeAnswerObjects(a, {
        [step.answerKey]: { ...(a[step.answerKey] || {}), [fieldId]: value }
      })
    );
  };
  const setDatePickerOpen = (fieldId, isOpen) => {
    setDatePickerOpenStates((prev) => ({ ...prev, [fieldId]: isOpen }));
  };
  return (
    <div ref={ref} className="stake-ai-controls">
      <div className="stake-ai-form">
        {shouldShowNotice && <PIIConsentNotice />}
        {isSubmittingContact ? (
          // Show single loading indicator when submitting contact info
          <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#f4fdfa", padding: "12px 0" }}>
            <span className="loading-dots">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </div>
        ) : (
          step.fields.map((f) => {
          const rawValue = payload[f.id] || "";
          const isDateField = f.inputType === "date";
          const isAddressField = f.inputType === "address" || (step.id === STEP_IDS.CASHBACK_BANK_ADDRESS && f.id === "address");
          const isSSNField = step.id === STEP_IDS.CASHBACK_BANK_SSN && f.id === "ssn";
          const isPhoneField = (step.id === STEP_IDS.CASHBACK_PHONE_INPUT || step.id === STEP_IDS.GLOBAL_MANUAL_CONTACT) && f.id === "phone";
          const isDOBField = (step.id === STEP_IDS.GLOBAL_DOB_CHECK || step.id === STEP_IDS.CASHBACK_BANK_DOB) && f.id === "dob";
          const dateValue = rawValue ? moment(rawValue) : null;
          const isDatePickerOpen = datePickerOpenStates[f.id] || false;
          
          // For address field, use rawValue directly (don't format) to prevent autofill conflicts
          // Only show formatted address when address was selected from autocomplete (all fields populated)
          const addressInputValue = isAddressField && payload.addressSelectedFromAutocomplete && payload.address && payload.city && payload.state && payload.zipCode
            ? `${payload.address}, ${payload.city}, ${payload.state}, ${payload.zipCode}`
            : rawValue;
          
          return (
            <div key={f.id} className="stake-ai-field">
              <label className="text-sm-medium color--mist" htmlFor={`chat-${f.id}`}>{t(f.labelKey)}</label>
              {isAddressField ? (
                <div style={{ width: "100%" }}>
                  <AddressAutoCompleteField
                    fieldProps={{
                      id: `chat-${f.id}`,
                      className: "stake-ai-input",
                      placeholder: f.placeholder ? t(f.placeholder) : "",
                      value: addressInputValue,
                      onChange: (e) => {
                        // Clear addressSelectedFromAutocomplete flag when user starts typing manually
                        if (payload.addressSelectedFromAutocomplete) {
                          updateField("addressSelectedFromAutocomplete", false);
                          // Clear individual address fields when user starts typing
                          updateField("address", "");
                          updateField("city", "");
                          updateField("state", "");
                          updateField("zipCode", "");
                        }
                        updateField(f.id, e.target.value);
                      },
                      variant: "filled",
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "transparent",
                          "& .MuiOutlinedInput-input": {
                            color: "#f4fdfa",
                            "&::placeholder": {
                              color: "rgba(244, 253, 250, 0.5)",
                              opacity: 1
                            }
                          }
                        },
                        "& .MuiInputBase-root": {
                          backgroundColor: "transparent",
                          "& .MuiInputBase-input": {
                            color: "#f4fdfa",
                            "&::placeholder": {
                              color: "rgba(244, 253, 250, 0.5)",
                              opacity: 1
                            }
                          }
                        }
                      }
                    }}
                    onAddressSelect={(selectedAddress) => {
                      updateField("address", selectedAddress.address);
                      updateField("city", selectedAddress.city);
                      updateField("state", selectedAddress.state);
                      updateField("zipCode", selectedAddress.zipCode);
                      // Mark that address was selected from Google autocomplete
                      updateField("addressSelectedFromAutocomplete", true);
                    }}
                  />
                </div>
              ) : isDateField ? (
                isSubmitting ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#f4fdfa", padding: "12px 0" }}>
                    <span className="loading-dots">
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  </div>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      value={dateValue}
                      format="MM/DD/YYYY"
                      open={isDatePickerOpen}
                      onOpen={() => setDatePickerOpen(f.id, true)}
                      onClose={() => setDatePickerOpen(f.id, false)}
                      onChange={(newValue) => {
                        if (newValue && moment(newValue).isValid()) {
                          updateField(f.id, moment(newValue).format("YYYY-MM-DD"));
                        } else {
                          updateField(f.id, "");
                        }
                      }}
                      disableFuture={isDOBField}
                      minDate={isDOBField ? moment('1900-01-01') : undefined}
                      maxDate={isDOBField ? moment().subtract(18, 'years') : undefined}
                      slotProps={{
                        textField: {
                          id: `chat-${f.id}`,
                          className: "stake-ai-input stake-ai-date-picker",
                          placeholder: f.placeholder ? t(f.placeholder) : "",
                          readOnly: true,
                          onClick: () => setDatePickerOpen(f.id, true),
                        InputProps: {
                          className: "stake-ai-date-picker-input"
                        },
                        inputProps: {
                          className: "stake-ai-date-picker-input-element"
                        },
                        sx: {
                          cursor: "pointer",
                          backgroundColor: "#ffffff",
                          "& .MuiOutlinedInput-root": {
                            cursor: "pointer",
                            backgroundColor: "#ffffff",
                            "& .MuiOutlinedInput-input": {
                              cursor: "pointer",
                              backgroundColor: "#ffffff",
                              color: "#1f2533"
                            }
                          },
                          "& .MuiInputBase-root": {
                            cursor: "pointer",
                            backgroundColor: "#ffffff",
                            "& .MuiInputBase-input": {
                              cursor: "pointer",
                              backgroundColor: "#ffffff",
                              color: "#1f2533"
                            }
                          }
                        }
                      }
                    }}
                  />
                  </LocalizationProvider>
                )
              ) : (
                isSubmitting && step.id === STEP_IDS.GLOBAL_MANUAL_CONTACT ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#f4fdfa", padding: "12px 0" }}>
                    <span className="loading-dots">
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  </div>
              ) : isPhoneField ? (
                <IMaskInput
                  id={`chat-${f.id}`}
                  className="stake-ai-input"
                  mask={phoneNumberMask}
                  value={rawValue}
                  eager={true}
                  onAccept={(value) => {
                    updateField(f.id, removeControlCharacters(value));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onSubmit();
                    }
                  }}
                />
              ) : (
                <input
                  id={`chat-${f.id}`}
                  className="stake-ai-input"
                  type={f.inputType || "text"}
                  value={rawValue}
                  maxLength={isSSNField ? 9 : undefined}
                  inputMode={isSSNField ? "numeric" : undefined}
                  pattern={isSSNField ? "[0-9]*" : undefined}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onSubmit();
                    }
                    // Only allow digits for SSN field
                    if (isSSNField && e.key !== "Enter" && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && !/^[0-9]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    if (isSSNField) {
                      // Only allow digits
                      const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 9);
                      updateField(f.id, digitsOnly);
                    } else {
                      updateField(f.id, e.target.value);
                    }
                  }}
                />
                )
              )}
            </div>
          );
        }))}
      </div>
      {step.requireConsent && step.consentKey && (
        <div className="stake-ai-field" style={{ marginTop: "16px" }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "12px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={payload?.consent === true}
              onChange={(e) => updateField("consent", e.target.checked)}
              style={{ 
                marginTop: "4px", 
                flexShrink: 0, 
                width: "20px", 
                height: "20px", 
                cursor: "pointer" 
              }}
            />
            <span className="text-xs color--mist" style={{ lineHeight: "1.5" }}>
              <Trans
                i18nKey={step.consentKey}
                components={{
                  privacy: (
                    <a className="color--primary-400" href={PRIVACY_URL} target="_blank" rel="noreferrer" />
                  ),
                  terms: (
                    <a className="color--primary-400" href={TERMS_URL} target="_blank" rel="noreferrer" />
                  )
                }}
              />
            </span>
          </label>
        </div>
      )}
      {!isSubmittingContact && (
      <div className="stake-ai-actions">
        <PrimaryButton onClick={onSubmit} disabled={!isStepValid || isSubmitting}>
          {t("manual.continue")}
        </PrimaryButton>
      </div>
      )}
    </div>
  );
});

function PIIConsentNotice() {
  return (
    <p className="text-xs color--mist" style={{ marginBottom: 12 }}>
      <Trans
        i18nKey="legal.piiNotice"
        components={{
          privacy: (
            <a className="color--primary-400" href={PRIVACY_URL} target="_blank" rel="noreferrer" />
          ),
          terms: (
            <a className="color--primary-400" href={TERMS_URL} target="_blank" rel="noreferrer" />
          )
        }}
      />
    </p>
  );
}



